import {
  AppBarEvents,
  LaunchTileEventOriginator,
} from "@1js/office-start-appbar-events";
import { AppBarInteractivity } from "@1js/office-start-appbar-types";
import { HarmonyThemeIdContext } from "@1js/office-start-style-utility";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Button, mergeClasses, Tooltip } from "@fluentui/react-components";
import { useFragment, useNovaEventing } from "@nova/react";
import { NovaEvent } from "@nova/types";
import React, { useContext, useMemo, useState } from "react";
import { TileDimensionDefaultValue, ViewMode } from "./Constants";
import useAppBarContext from "./hooks/useAppBarContext";
import { useLaunchTileStyles } from "./LaunchTile.styles";
import {
  behaviorFragment as LaunchTile_launchTileBehavior,
  dataFragment as LaunchTile_m365App,
  LaunchTileProps,
} from "./LaunchTile.types";
import { usePermissionsCallback } from "./SuggestedAppTile";
import { TileBadge } from "./TileBadge";
import { TileContextMenuCallbacks } from "./TileContextMenu.types";
import { TileIcon } from "./TileIcon";
const useLaunchCallback = (
  id: string,
  platformType: string | undefined,
  tileElementID: string | undefined,
  launchOut: boolean,
  tilePosition: number,
  iconPositionVertical: (tilePosition: number) => number | undefined,
  tileHostContext: string,
  context: unknown | undefined,
  hasEvented: React.MutableRefObject<boolean>
) => {
  const novaEventing = useNovaEventing();
  const launchCallback = React.useCallback(
    async (reactEvent?: React.SyntheticEvent) => {
      hasEvented.current = true;
      const launchEvent = AppBarEvents.launch(() => {
        return {
          reactEvent: reactEvent,
          tileID: id,
          platformType: platformType,
          tileElementID: tileElementID,
          launchOut: launchOut,
          tilePosition: tilePosition,
          tilePositionVertical: iconPositionVertical(tilePosition),
          tileHostContext: tileHostContext,
          launchContext: context,
        };
      });
      if (reactEvent) {
        novaEventing.bubble({
          event: launchEvent,
          reactEvent,
        });
      } else {
        novaEventing.generateEvent({
          event: launchEvent,
        });
      }
    },
    [
      hasEvented,
      id,
      platformType,
      tileElementID,
      launchOut,
      tilePosition,
      iconPositionVertical,
      tileHostContext,
      context,
      novaEventing,
    ]
  );
  return { launchCallback };
};
const useContentDroppedCallback = (id: string) => {
  const novaEventing = useNovaEventing();
  const contentDroppedCallback = React.useCallback(
    async (reactEvent: React.DragEvent) => {
      novaEventing.bubble({
        event: AppBarEvents.contentDropped(() => {
          return {
            tileID: id,
            dropData: reactEvent.dataTransfer,
          };
        }),
        reactEvent,
      });
    },
    [id, novaEventing]
  );
  return { contentDroppedCallback };
};
const useGetMoreInfoCallback = (id: string, context: unknown | undefined) => {
  const novaEventing = useNovaEventing();
  const moreInfoCallback = React.useCallback(
    async (reactEvent: React.SyntheticEvent) => {
      novaEventing.bubble({
        event: AppBarEvents.moreInfo(() => {
          return {
            tileID: id,
            context,
          };
        }),
        reactEvent,
      });
    },
    [id, context, novaEventing]
  );
  return { moreInfoCallback };
};
const useUninstallCallback = (
  id: string,
  name: string,
  isInUse: boolean,
  context: unknown | undefined
) => {
  const novaEventing = useNovaEventing();
  const uninstallCallback = React.useCallback(
    async (reactEvent: React.SyntheticEvent) => {
      novaEventing.bubble({
        event: AppBarEvents.uninstall(() => {
          return {
            isInUse,
            tileID: id,
            appName: name,
            context,
          };
        }),
        reactEvent,
      });
    },
    [id, name, novaEventing, isInUse, context]
  );
  return { uninstallCallback };
};
/**
 * Launch Tile React functional component.
 *
 */
export const LaunchTileRender: React.ForwardRefRenderFunction<
  AppBarInteractivity,
  LaunchTileProps
> = (
  {
    index,
    viewMode,
    launchTileBehavior: launchTileBehaviorRef,
    m365App: m365AppRef,
    assignShortcutHandler,
    unassignShortcutHandler,
    onPinStateChange,
    onClicked,
    onContextMenu,
    linkedEntity,
    dragDropEnabled,
    enableMicaV2DesktopStyles,
    enableMicaV2WebStyles,
    enableMercuryDesignStyles,
    backgroundShade,
    isListItem,
    enableVisualRefreshStyles,
  },
  ref
) => {
  const {
    id,
    platformType,
    name,
    accessibleName,
    icon,
    outlineIcon,
    colorIconSmall,
    badge,
    selectedStateIconOverride,
    isInUse,
    canLaunchOut,
    isPinnable,
    enablePlatformCommands,
    canBeUnacquired,
    hasPermissions,
    pinnedIndex,
    secondaryActions,
    brandingColor,
    allowedExternalDropContent,
  } = useFragment(LaunchTile_m365App, m365AppRef);
  const {
    transparent,
    pinningEnabled,
    appTilePositionEnabled,
    platformAppCommands,
    useHubColorForSelectedTileLabel,
    useOutlineIcon,
    useSmallColorIcon,
  } = useFragment(LaunchTile_launchTileBehavior, launchTileBehaviorRef);
  const readerBoxId = `readerBox-${id}`;
  const tileStyles = useLaunchTileStyles();
  const [isHovered, setIsHovered] = useState(false);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const hasEvented = React.useRef(false);
  const buttonRef = React.useRef<HTMLAnchorElement | HTMLButtonElement | null>(
    null
  );
  const launchTileContainerRef = React.useRef<HTMLDivElement | null>(null);
  const { listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: id, disabled: !dragDropEnabled });
  const appBarContext = useAppBarContext();
  const containerStyle = React.useMemo(() => {
    return {
      transform: CSS.Transform.toString(transform),
      transition,
    };
  }, [transform, transition]);
  const isHarmonyThemeId = useContext(HarmonyThemeIdContext);
  const isCommonButton =
    !enableMicaV2DesktopStyles &&
    !enableMicaV2WebStyles &&
    viewMode !== ViewMode.Flyout;
  const buttonStyles = useMemo(() => {
    return mergeClasses(
      tileStyles.button,
      tileStyles.tileFocusIndicator,
      isCommonButton && tileStyles.buttonCommon,
      isCommonButton && backgroundShade === "ThreeColumnView"
        ? tileStyles.buttonCommonThreeColumnView
        : tileStyles.buttonCommonTwoColumnView,
      enableMicaV2DesktopStyles && tileStyles.micaButtonV2Desktop,
      enableMicaV2WebStyles && tileStyles.micaButtonV2Web,
      enableMercuryDesignStyles && tileStyles.mercuryDesign,
      viewMode === ViewMode.Wide && tileStyles.wideButton,
      viewMode === ViewMode.Flyout && tileStyles.flyoutTile,
      !!transparent && tileStyles.transparentBackground,
      !!isInUse && tileStyles.selected,
      !!isInUse &&
        ((enableMicaV2DesktopStyles && tileStyles.micaSelectedV2Desktop) ||
          (enableMicaV2WebStyles && tileStyles.micaSelectedV2Web)),
      !!isInUse &&
        enableMercuryDesignStyles &&
        tileStyles.mercuryDesignSelected,
      isDragging && tileStyles.dragging,
      isDraggedOver &&
        isCommonButton &&
        backgroundShade === "ThreeColumnView" &&
        tileStyles.buttonCommonThreeColumnViewDragOver,
      isDraggedOver &&
        isCommonButton &&
        backgroundShade === "TwoColumnView" &&
        tileStyles.buttonCommonTwoColumnViewDragOver,
      isDraggedOver &&
        enableMicaV2DesktopStyles &&
        tileStyles.micaButtonV2DesktopDragOver,
      isDraggedOver &&
        enableMicaV2WebStyles &&
        tileStyles.micaButtonV2WebDragOver,
      isDraggedOver &&
        enableMercuryDesignStyles &&
        tileStyles.mercuryTileDragOver,
      isDraggedOver &&
        viewMode === ViewMode.Flyout &&
        tileStyles.flyoutTileDragOver,
      enableVisualRefreshStyles && tileStyles.roundedTileButton,
      !!isInUse &&
        enableVisualRefreshStyles &&
        tileStyles.roundedTileButtonSelected,
      !!isHarmonyThemeId &&
        enableVisualRefreshStyles &&
        tileStyles.visualRefreshSelectionBar
    );
  }, [
    tileStyles.button,
    tileStyles.tileFocusIndicator,
    tileStyles.buttonCommon,
    tileStyles.buttonCommonThreeColumnView,
    tileStyles.buttonCommonTwoColumnView,
    tileStyles.buttonCommonThreeColumnViewDragOver,
    tileStyles.buttonCommonTwoColumnViewDragOver,
    tileStyles.micaButtonV2Desktop,
    tileStyles.micaButtonV2Web,
    tileStyles.mercuryDesign,
    tileStyles.mercuryTileDragOver,
    tileStyles.micaButtonV2DesktopDragOver,
    tileStyles.micaButtonV2WebDragOver,
    tileStyles.roundedTileButton,
    tileStyles.roundedTileButtonSelected,
    tileStyles.wideButton,
    tileStyles.flyoutTile,
    tileStyles.flyoutTileDragOver,
    tileStyles.transparentBackground,
    tileStyles.selected,
    tileStyles.micaSelectedV2Desktop,
    tileStyles.micaSelectedV2Web,
    tileStyles.mercuryDesignSelected,
    tileStyles.visualRefreshSelectionBar,
    tileStyles.dragging,
    isCommonButton,
    backgroundShade,
    enableMicaV2DesktopStyles,
    enableMicaV2WebStyles,
    enableMercuryDesignStyles,
    enableVisualRefreshStyles,
    viewMode,
    transparent,
    isInUse,
    isDragging,
    isDraggedOver,
    isHarmonyThemeId,
  ]);
  // Checks if the dragged content exists, and is allowed to be dropped on this tile
  const checkIsAllowedContentDropType = React.useCallback(
    (ev: React.DragEvent) => {
      return (
        ev?.dataTransfer &&
        allowedExternalDropContent?.some((type: string) =>
          ev.dataTransfer.types.includes(type)
        )
      );
    },
    [allowedExternalDropContent]
  );
  const enableContextMenu =
    canLaunchOut ||
    (pinningEnabled && isPinnable) ||
    (enablePlatformCommands && platformAppCommands?.aboutCommandEnabled) ||
    (enablePlatformCommands &&
      canBeUnacquired &&
      platformAppCommands?.uninstallCommandEnabled) ||
    (secondaryActions && secondaryActions.length > 0);
  const getSelectedItemVerticalPosition = React.useCallback(
    (tilePosition: number) => {
      if (!appTilePositionEnabled) {
        return undefined;
      }
      const pinnedItemsLength = appBarContext.pinnedItems?.length ?? -1;
      const pinnedIndex =
        Math.min(appBarContext.overflowIndex ?? 99999, pinnedItemsLength) - 1;
      const lastPinnedApp =
        pinnedItemsLength > 0
          ? appBarContext.pinnedItems?.[pinnedIndex]
          : undefined;
      // If we are dealing with an app in the overflow that is about to be temporarily pinned
      // we calculate the offset of where it'll be placed
      const appIsHoisted = tilePosition === appBarContext.overflowIndex;
      if (lastPinnedApp && viewMode === ViewMode.Flyout && !appIsHoisted) {
        const topPixel =
          lastPinnedApp.itemRef.current?.getAppIconPositionRect()?.top;
        return topPixel
          ? topPixel + TileDimensionDefaultValue * 1.5
          : undefined;
      }
      let appbarItem = appIsHoisted
        ? appBarContext.hoistedItem
        : appBarContext.pinnedItems?.[tilePosition];
      // If this is not from a click, prefer the selected item on the context
      if (hasEvented.current === false) {
        appbarItem = appBarContext.selectedItem;
      }
      return appbarItem?.itemRef.current?.getAppIconPositionRect()?.top;
    },
    [
      appTilePositionEnabled,
      appBarContext.pinnedItems,
      appBarContext.overflowIndex,
      appBarContext.hoistedItem,
      appBarContext.selectedItem,
      viewMode,
    ]
  );
  const { launchCallback: launchOutCallback } = useLaunchCallback(
    id,
    platformType ?? undefined,
    buttonRef.current?.id,
    true,
    index,
    getSelectedItemVerticalPosition,
    viewMode === ViewMode.Flyout ? "Flyout" : "AppBar",
    linkedEntity,
    hasEvented
  );
  const { launchCallback: launchInlineCallback } = useLaunchCallback(
    id,
    platformType ?? undefined,
    buttonRef.current?.id,
    false,
    index,
    getSelectedItemVerticalPosition,
    viewMode === ViewMode.Flyout ? "Flyout" : "AppBar",
    linkedEntity,
    hasEvented
  );
  const { contentDroppedCallback } = useContentDroppedCallback(id);
  const { moreInfoCallback } = useGetMoreInfoCallback(id, linkedEntity);
  const { permissionsCallback } = usePermissionsCallback(id);
  const { uninstallCallback } = useUninstallCallback(
    id,
    name,
    !!isInUse,
    linkedEntity
  );
  const novaEventing = useNovaEventing();
  const useCustomActionCallback = React.useCallback(
    (
      reactEvent: React.SyntheticEvent,
      eventType: string,
      eventData: string
    ) => {
      const novaEvent: NovaEvent<unknown> = {
        originator: LaunchTileEventOriginator,
        type: eventType,
        data: () => eventData,
      };
      novaEventing.bubble({
        event: novaEvent,
        reactEvent,
      });
    },
    [novaEventing]
  );
  /**
   * Tile click handler
   *
   * @param {React.MouseEvent<HTMLElement>} [ev] - Click event from the tile
   */
  const useTileClickCallback = React.useCallback(
    (ev?: React.MouseEvent<HTMLElement>) => {
      if (window.performance) {
        window.performance.mark("TileClicked");
      }
      if (ev) {
        if (ev.ctrlKey || ev.shiftKey || ev.metaKey) {
          // The control/shift/command key was pressed while clicking the tile, so launch out
          launchOutCallback(ev);
        } else {
          launchInlineCallback(ev);
          if (onClicked && !ev.defaultPrevented) {
            onClicked();
          }
        }
        ev.stopPropagation();
      }
    },
    [launchOutCallback, launchInlineCallback, onClicked]
  );
  const useTileAuxClickCallback = React.useCallback(
    (ev?: React.MouseEvent<HTMLElement>) => {
      if (ev) {
        if (ev.button === 1) {
          // Middle mouse button should launch out
          launchOutCallback(ev);
        }
        ev.stopPropagation();
      }
    },
    [launchOutCallback]
  );
  const useTileContainerClickCallback = React.useCallback(
    (_ev?: React.SyntheticEvent<HTMLElement, Event>) => {
      if (buttonRef && buttonRef.current) {
        // Let the app tile container handle the click from the badge
        // and proxy the click and focus to the underlying button
        buttonRef.current.focus();
        buttonRef.current.click();
      }
    },
    [buttonRef]
  );
  const useShortcutCallback = React.useCallback(
    (ev?: React.KeyboardEvent<HTMLElement>) => {
      launchInlineCallback(ev);
    },
    [launchInlineCallback]
  );
  // Add/Remove shortcut handlers
  React.useEffect(() => {
    // overflow index needs to be calculated before we assign shortcut handlers to the launch tiles
    // otherwise we risk having the same index for multiple tiles.
    if (
      assignShortcutHandler &&
      appBarContext.overflowIndex !== undefined &&
      index < appBarContext.overflowIndex
    ) {
      assignShortcutHandler(index + 1, useShortcutCallback);
    }
    return () => {
      if (unassignShortcutHandler) {
        unassignShortcutHandler(index + 1, useShortcutCallback);
      }
    };
  }, [
    appBarContext.overflowIndex,
    assignShortcutHandler,
    index,
    unassignShortcutHandler,
    useShortcutCallback,
  ]);
  // In order to fire the launch event for when the tile is selected through some other means
  // that isn't a user click on the tile we have this case, where if we haven't evented yet from
  // a click, and the tile is selected, emit a launch event.
  React.useEffect(() => {
    const selectedItem = appBarContext.selectedItem;
    if (!selectedItem || !appTilePositionEnabled) return;
    if (selectedItem.data.node.id === id && hasEvented.current === false) {
      launchInlineCallback();
      hasEvented.current = true;
    } else {
      hasEvented.current = false;
    }
  }, [
    novaEventing,
    appTilePositionEnabled,
    appBarContext.selectedItem,
    id,
    launchInlineCallback,
  ]);
  const contextualCommands: TileContextMenuCallbacks = React.useMemo(() => {
    return {
      onLaunchOut: canLaunchOut ? launchOutCallback : undefined,
      onPermissionsClick: hasPermissions ? permissionsCallback : undefined,
      onGetMoreInfo:
        enablePlatformCommands && platformAppCommands?.aboutCommandEnabled
          ? moreInfoCallback
          : undefined,
      onPinStateChange:
        pinningEnabled && isPinnable ? onPinStateChange : undefined,
      onUninstall:
        enablePlatformCommands &&
        canBeUnacquired &&
        platformAppCommands?.uninstallCommandEnabled
          ? uninstallCallback
          : undefined,
      onCustomAction: useCustomActionCallback,
      customActions: secondaryActions,
    };
  }, [
    canLaunchOut,
    secondaryActions,
    isPinnable,
    enablePlatformCommands,
    launchOutCallback,
    permissionsCallback,
    moreInfoCallback,
    onPinStateChange,
    pinningEnabled,
    canBeUnacquired,
    hasPermissions,
    platformAppCommands,
    useCustomActionCallback,
    uninstallCallback,
  ]);
  const onTileContextMenu = React.useCallback(
    (evt: React.MouseEvent<HTMLElement>) => {
      if (!enableContextMenu) {
        evt.preventDefault();
        return;
      }
      evt.preventDefault();
      if (onContextMenu) {
        onContextMenu(
          evt.currentTarget,
          pinnedIndex ?? undefined,
          contextualCommands,
          id
        );
      }
    },
    [contextualCommands, enableContextMenu, id, onContextMenu, pinnedIndex]
  );
  const onTileMouseOver = React.useCallback(() => {
    setIsHovered(true);
  }, []);
  const onTileMouseLeave = React.useCallback(() => {
    setIsHovered(false);
  }, []);
  const onTileDragOver = React.useCallback(
    (ev: React.DragEvent) => {
      ev.preventDefault();
      if (!checkIsAllowedContentDropType(ev)) {
        // Render a no-drop cursor
        ev.dataTransfer.dropEffect = "none";
      } else {
        setIsDraggedOver(true);
        setIsHovered(true);
      }
    },
    [checkIsAllowedContentDropType]
  );
  const onTileDragLeave = React.useCallback((_ev: React.DragEvent) => {
    setIsDraggedOver(false);
    setIsHovered(false);
  }, []);
  const onTileDrop = React.useCallback(
    (ev: React.DragEvent) => {
      ev.preventDefault();
      ev.stopPropagation();
      setIsDraggedOver(false);
      contentDroppedCallback(ev);
    },
    [contentDroppedCallback]
  );
  const buttonBehavior = useMemo<React.HTMLAttributes<HTMLElement>>(() => {
    let externalDragAndDropListeners = {};
    if (allowedExternalDropContent) {
      externalDragAndDropListeners = {
        onDragOver: onTileDragOver,
        onDragLeave: onTileDragLeave,
        onDrop: onTileDrop,
      };
    }
    return {
      onMouseOver: onTileMouseOver,
      onMouseLeave: onTileMouseLeave,
      onContextMenu: onTileContextMenu,
      ...externalDragAndDropListeners,
    };
  }, [
    allowedExternalDropContent,
    onTileContextMenu,
    onTileMouseLeave,
    onTileMouseOver,
    onTileDragOver,
    onTileDragLeave,
    onTileDrop,
  ]);
  const tileIcon = useMemo(() => {
    let iconToUse: typeof icon;
    if (enableVisualRefreshStyles && (isInUse || isHovered)) {
      iconToUse = selectedStateIconOverride ?? icon;
    } else if (viewMode === ViewMode.Flyout) {
      iconToUse = icon;
    } else if (useOutlineIcon && outlineIcon) {
      iconToUse = outlineIcon;
    } else if (
      !!useSmallColorIcon &&
      colorIconSmall &&
      viewMode !== ViewMode.Flyout
    ) {
      iconToUse = colorIconSmall;
    } else {
      iconToUse =
        isInUse || isHovered ? (selectedStateIconOverride ?? icon) : icon;
    }
    return iconToUse;
  }, [
    icon,
    isHovered,
    isInUse,
    selectedStateIconOverride,
    enableVisualRefreshStyles,
    useOutlineIcon,
    outlineIcon,
    useSmallColorIcon,
    colorIconSmall,
    viewMode,
  ]);
  React.useEffect(() => {
    if (launchTileContainerRef.current) {
      setNodeRef(launchTileContainerRef.current);
    }
  }, [launchTileContainerRef, setNodeRef]);
  const tileIconComponent = useMemo(
    () => (
      <TileIcon
        id={id}
        icon={tileIcon}
        viewMode={viewMode}
        label={name}
        isSelected={isInUse || isHovered}
        brandingColor={brandingColor ?? null}
        useHubColorForSelectedTileLabel={!!useHubColorForSelectedTileLabel}
        useOutlineIconFilter={
          !!useOutlineIcon &&
          outlineIcon != null &&
          viewMode !== ViewMode.Flyout &&
          !(enableVisualRefreshStyles && (isInUse || isHovered))
        }
        useSmallColorIconFilter={
          !useOutlineIcon &&
          !!useSmallColorIcon &&
          colorIconSmall != null &&
          viewMode !== ViewMode.Flyout &&
          !(enableVisualRefreshStyles && (isInUse || isHovered))
        }
        showDotIndicator={platformType === "m365_messageextension"}
        useMicaStyles={enableMicaV2DesktopStyles || enableMicaV2WebStyles}
        enableMercuryDesignStyles={enableMercuryDesignStyles}
        enableVisualRefreshStyles={enableVisualRefreshStyles}
        isGradientIconEnabled={!!isHarmonyThemeId && enableVisualRefreshStyles}
      />
    ),
    [
      brandingColor,
      enableMercuryDesignStyles,
      enableMicaV2DesktopStyles,
      enableMicaV2WebStyles,
      enableVisualRefreshStyles,
      id,
      isHovered,
      isInUse,
      name,
      outlineIcon,
      platformType,
      tileIcon,
      useHubColorForSelectedTileLabel,
      useOutlineIcon,
      useSmallColorIcon,
      colorIconSmall,
      viewMode,
      isHarmonyThemeId,
    ]
  );
  const tileButton = useMemo(() => {
    return (
      <Button
        id={id}
        data-tid={id}
        ref={buttonRef}
        aria-label={accessibleName || undefined}
        aria-pressed={
          viewMode !== ViewMode.Flyout && !isListItem ? !!isInUse : undefined
        }
        aria-describedby={readerBoxId}
        role={isListItem ? "listitem" : "button"}
        data-clicklog="custom"
        data-pinned={`${!!pinnedIndex}`}
        icon={tileIconComponent}
        className={buttonStyles}
        onClick={useTileClickCallback}
        onAuxClick={useTileAuxClickCallback}
        {...buttonBehavior}
      />
    );
  }, [
    id,
    accessibleName,
    viewMode,
    isListItem,
    isInUse,
    readerBoxId,
    tileIconComponent,
    buttonStyles,
    pinnedIndex,
    useTileClickCallback,
    useTileAuxClickCallback,
    buttonBehavior,
  ]);
  React.useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        if (buttonRef?.current) {
          buttonRef.current.focus();
        }
      },
      getAppIconPositionRect: () => {
        return buttonRef?.current?.getBoundingClientRect();
      },
      getAppIconElementRef: () => {
        return buttonRef;
      },
    }),
    [buttonRef]
  );
  const badgeControl = useMemo(() => {
    if (!badge) {
      return null;
    }
    let renderBadge = true;
    // if we are getting passed a numeric badge with a count of 0, we don't want to render it
    if (
      badge.__typename === "M365ApplicationNumericBadge" &&
      (!badge.count || badge.count === 0)
    ) {
      renderBadge = false;
    }
    return (
      renderBadge && (
        <TileBadge
          badge={badge}
          readerBoxId={readerBoxId}
          transparentBorder={!!transparent}
          viewMode={viewMode}
          isDragging={isDragging}
          enableMercuryDesignStyles={enableMercuryDesignStyles}
        />
      )
    );
  }, [
    badge,
    enableMercuryDesignStyles,
    isDragging,
    readerBoxId,
    transparent,
    viewMode,
  ]);
  return (
    // Note: the tile container needs to act like the tile button
    // so it can properly handle events bubbled up from the badge
    // TODO: Optimize fluent tooltip so we don't have to have a split statement like this.
    <>
      {viewMode === ViewMode.Flyout ? (
        <div
          className={mergeClasses(
            viewMode === ViewMode.Flyout
              ? tileStyles.flyoutTileContainer
              : tileStyles.tileContainer,
            enableVisualRefreshStyles && tileStyles.roundedTileButtonContainer
          )}
          onClick={useTileContainerClickCallback}
          role="none"
          {...buttonBehavior}
          {...listeners}
          ref={launchTileContainerRef}
          style={containerStyle}
          title={accessibleName ?? name}
        >
          {tileButton}
          {badge && badgeControl}
        </div>
      ) : (
        <Tooltip
          content={{
            children: accessibleName ?? name,
          }}
          positioning={viewMode === ViewMode.Flyout ? "above" : "after"}
          relationship="label"
        >
          <div
            className={mergeClasses(
              viewMode === ViewMode.Flyout
                ? tileStyles.flyoutTileContainer
                : tileStyles.tileContainer,
              enableVisualRefreshStyles && tileStyles.roundedTileButtonContainer
            )}
            onClick={useTileContainerClickCallback}
            role="none"
            {...buttonBehavior}
            {...listeners}
            ref={launchTileContainerRef}
            style={containerStyle}
          >
            {tileButton}
            {badge && badgeControl}
          </div>
        </Tooltip>
      )}
    </>
  );
};
LaunchTileRender.displayName = "LaunchTile";
export const LaunchTile = React.forwardRef(LaunchTileRender);
